import React, { useState, useEffect, useRef } from 'react';
import { Package, Clock, CheckCircle, XCircle, Phone, MapPin, CreditCard, Wallet, Printer, Trash2, Plus, Pencil, Palmtree } from 'lucide-react';
import { toast } from 'sonner';
// Legacy iframe-based print utility is no longer used here — handlePrintOrder
// now opens the standalone /print/:orderNumber route which fires rawbt:base64.
// utils/printReceipt.js is kept on disk as a fallback while the new path is
// validated on production tablets.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ORDER_TIMEZONE = 'Europe/Budapest';

/**
 * Inline-editable text/number field used in the product management list.
 * Click to edit; Enter (or blur) to save; Escape to cancel.
 */
const InlineEditableField = ({ value, onSave, type = 'text', suffix = '', placeholder = '', className = '', multiline = false, ariaLabel }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const commit = async () => {
    if (saving) return;
    const normalized = type === 'number' ? parseInt(draft, 10) : String(draft).trim();
    if (type === 'number' && (Number.isNaN(normalized) || normalized < 0)) {
      toast.error('Az ár nem lehet negatív vagy üres szám');
      setEditing(false);
      setDraft(value ?? '');
      return;
    }
    if (normalized === value || normalized === '') {
      setEditing(false);
      setDraft(value ?? '');
      return;
    }
    setSaving(true);
    const ok = await onSave(normalized);
    setSaving(false);
    if (ok) {
      setEditing(false);
    } else {
      setDraft(value ?? '');
    }
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value ?? '');
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commit();
          }}
          rows={2}
          className={`w-full px-2 py-1 border-2 border-purple-400 rounded outline-none focus:border-purple-600 ${className}`}
          aria-label={ariaLabel}
        />
      );
    }
    return (
      <input
        autoFocus
        type={type}
        min={type === 'number' ? 0 : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
        placeholder={placeholder}
        className={`px-2 py-1 border-2 border-purple-400 rounded outline-none focus:border-purple-600 ${className}`}
        aria-label={ariaLabel}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`group inline-flex items-center text-left hover:bg-purple-50 rounded px-1 py-0.5 transition-colors ${className}`}
      title="Kattints a szerkesztéshez"
    >
      <span>{value || <span className="text-gray-400 italic">{placeholder || '(üres)'}</span>}{suffix}</span>
      <Pencil className="w-3 h-3 ml-1 text-gray-300 group-hover:text-purple-500 transition-colors" />
    </button>
  );
};

/**
 * Inline form for adding a new product to a category. Toggles between a
 * compact "+ Új termék" button and a full form.
 */
const NewProductForm = ({ categoryId, onCreate }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [productId, setProductId] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName(''); setPrice(''); setDescription(''); setProductId('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim() || !productId.trim() || price === '') {
      toast.error('Név, ár és termékkód kötelező');
      return;
    }
    const priceInt = parseInt(price, 10);
    if (Number.isNaN(priceInt) || priceInt < 0) {
      toast.error('Az ár nem lehet negatív');
      return;
    }
    setBusy(true);
    const ok = await onCreate(categoryId, {
      name: name.trim(),
      price: priceInt,
      description: description.trim(),
      productId: productId.trim(),
      isAvailable: true
    });
    setBusy(false);
    if (ok) {
      reset();
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center space-x-2 px-3 py-2 text-sm font-semibold text-purple-700 bg-purple-50 border-2 border-dashed border-purple-300 rounded-lg hover:bg-purple-100 transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span>Új termék hozzáadása</span>
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 p-3 bg-purple-50 border-2 border-purple-300 rounded-lg space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Termék neve"
          required
          className="px-3 py-2 border border-gray-300 rounded outline-none focus:border-purple-500"
        />
        <input
          type="text"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          placeholder="Termékkód (pl. langos-uj)"
          required
          className="px-3 py-2 border border-gray-300 rounded outline-none focus:border-purple-500"
        />
        <input
          type="number"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Ár (Ft)"
          required
          className="px-3 py-2 border border-gray-300 rounded outline-none focus:border-purple-500"
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Leírás (opcionális)"
          className="px-3 py-2 border border-gray-300 rounded outline-none focus:border-purple-500"
        />
      </div>
      <div className="flex space-x-2">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 bg-purple-600 text-white font-semibold rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {busy ? 'Mentés...' : 'Hozzáadás'}
        </button>
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded hover:bg-gray-300"
        >
          Mégse
        </button>
      </div>
    </form>
  );
};

const Admin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [restaurantOpen, setRestaurantOpen] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [takeawayEnabled, setTakeawayEnabled] = useState(false);
  const [dineinEnabled, setDineinEnabled] = useState(false);
  // Vacation ("szabadság") controls — drive the public site-wide pop-up.
  const [vacationEnabled, setVacationEnabled] = useState(false);
  const [vacationStart, setVacationStart] = useState('');
  const [vacationEnd, setVacationEnd] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0
  });

  // Product management states
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showProductManagement, setShowProductManagement] = useState(false);

  const ordersRequestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    // Initial load — wait for all three before clearing loading state, so the
    // control panel never flashes a stale "ZÁRVA" before the real status arrives.
    const initialLoad = async () => {
      try {
        await Promise.all([fetchOrders(), fetchStats(), fetchRestaurantStatus()]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    initialLoad();

    // Loading duration tick (1s) — only meaningful while `loading` is true.
    // Continues running but the JSX only consults it inside the `if (loading)` branch.
    const durationInterval = setInterval(() => {
      setLoadingDuration((d) => d + 1);
    }, 1000);

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchOrders();
      fetchStats();
    }, 10000);
    // Update clock every second
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearInterval(clockInterval);
      clearInterval(durationInterval);
    };
  }, [selectedStatus]);

  const fetchRestaurantStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/restaurant/status`);
      if (response.ok) {
        const data = await response.json();
        setRestaurantOpen(data.isOpen ?? false);
        setDeliveryEnabled(data.deliveryEnabled ?? false);
        setTakeawayEnabled(data.takeawayEnabled ?? false);
        setDineinEnabled(data.dineinEnabled ?? false);
        setVacationEnabled(data.vacationEnabled ?? false);
        setVacationStart(data.vacationStart ?? '');
        setVacationEnd(data.vacationEnd ?? '');
      } else {
        setRestaurantOpen(false);
        setDeliveryEnabled(false);
        setTakeawayEnabled(false);
        setDineinEnabled(false);
      }
    } catch (error) {
      console.error('Error fetching restaurant status:', error);
      setRestaurantOpen(false);
      setDeliveryEnabled(false);
      setTakeawayEnabled(false);
      setDineinEnabled(false);
    }
  };

  const updateStatus = async (patch, successMsg) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/restaurant/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      if (response.ok) {
        const data = await response.json();
        setRestaurantOpen(data.isOpen ?? false);
        setDeliveryEnabled(data.deliveryEnabled ?? false);
        setTakeawayEnabled(data.takeawayEnabled ?? false);
        setDineinEnabled(data.dineinEnabled ?? false);
        setVacationEnabled(data.vacationEnabled ?? false);
        setVacationStart(data.vacationStart ?? '');
        setVacationEnd(data.vacationEnd ?? '');
        toast.success(successMsg);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating restaurant status:', error);
      toast.error('Hiba a státusz frissítése során');
    }
  };

  // Persist a vacation date field immediately (mirrors the per-control save
  // pattern the status toggles above already use).
  const handleVacationDate = (field, value) => {
    if (field === 'vacationStart') setVacationStart(value);
    else setVacationEnd(value);
    updateStatus({ [field]: value }, 'Szabadság dátum mentve');
  };

  const toggleVacation = () => {
    const next = !vacationEnabled;
    if (next && (!vacationStart || !vacationEnd)) {
      toast.error('Add meg a kezdő és a záró dátumot a szabadság bekapcsolása előtt.');
      return;
    }
    if (next && vacationEnd < vacationStart) {
      toast.error('A záró dátum nem lehet korábbi a kezdő dátumnál.');
      return;
    }
    updateStatus(
      { vacationEnabled: next, vacationStart, vacationEnd },
      next
        ? 'Szabadság BEKAPCSOLVA — a vásárlók pop-up értesítést látnak'
        : 'Szabadság kikapcsolva'
    );
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Hiba a termékek betöltése során');
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleProductAvailability = async (productId, currentStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isAvailable: !currentStatus })
      });

      if (response.ok) {
        toast.success('Termék elérhetősége frissítve!');
        fetchProducts(); // Reload products
      } else {
        throw new Error('Failed to update product');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Hiba a termék frissítése során');
    }
  };

  // Generic single-field update (used by inline editors for name / price / description).
  const updateProductField = async (productId, field, value) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update');
      }
      toast.success('Termék frissítve!');
      fetchProducts();
      return true;
    } catch (error) {
      console.error('Error updating product field:', error);
      toast.error(`Hiba: ${error.message || 'frissítés sikertelen'}`);
      return false;
    }
  };

  const deleteProduct = async (productId, productName) => {
    if (!window.confirm(`Biztosan törlöd: "${productName}"? Ez a művelet nem visszavonható.`)) {
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete');
      }
      toast.success('Termék törölve!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(`Hiba a törlés során: ${error.message}`);
    }
  };

  const createProduct = async (categoryId, payload) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${categoryId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create');
      }
      toast.success('Termék létrehozva!');
      fetchProducts();
      return true;
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(`Hiba: ${error.message || 'létrehozás sikertelen'}`);
      return false;
    }
  };

  const fetchOrders = async () => {
    const requestId = ++ordersRequestIdRef.current;
    try {
      const params = new URLSearchParams({ today: 'true' });
      if (selectedStatus !== 'all') params.set('status', selectedStatus);
      const response = await fetch(`${BACKEND_URL}/api/orders?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        if (requestId !== ordersRequestIdRef.current) {
          return;
        }
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Hiba a rendelések betöltése során');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/stats/today`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateOrderStatus = async (orderNumber, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/${orderNumber}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Rendelés státusza frissítve!');
        fetchOrders();
        fetchStats();
      } else {
        throw new Error('Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Hiba a státusz frissítése során');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      accepted: 'bg-green-100 text-green-800 border-green-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      preparing: 'bg-purple-100 text-purple-800 border-purple-300',
      ready: 'bg-green-100 text-green-800 border-green-300',
      completed: 'bg-gray-100 text-gray-800 border-gray-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Új rendelés',
      accepted: 'Elfogadva',
      confirmed: 'Megerősítve',
      preparing: 'Készítés alatt',
      ready: 'Kész',
      completed: 'Teljesítve',
      rejected: 'Visszautasítva',
      cancelled: 'Törölve'
    };
    return labels[status] || status;
  };

  const getDeliveryTypeLabel = (type) => {
    const labels = {
      takeaway: 'Elvitel',
      delivery: 'Házhozszállítás',
      dinein: 'Helyben fogyasztás'
    };
    return labels[type] || type;
  };

  // Opens the standalone /print/:orderNumber page in a new tab, which fires
  // the rawbt:base64 URI scheme directly (auto=rawbt). This bypasses the
  // Android Chrome → PDF → RAWBT pipeline that produced microscopic/faint
  // output. The legacy `printReceipt(order)` iframe flow remains in
  // utils/printReceipt.js as a fallback while we validate the new path on
  // production tablets.
  const handlePrintOrder = (order) => {
    window.open(`/print/${order.orderNumber}?auto=rawbt`, '_blank');
  };

  if (loading) {
    // Time-aware loading message — the backend runs on Render's free tier and
    // can take 3–4 minutes to wake up after idle. Without this feedback the
    // owner thought the page was frozen.
    let loadingTitle = 'Betöltés...';
    let loadingDetail = '';
    if (loadingDuration >= 30) {
      loadingTitle = 'A szerver alszik — felébresztése folyamatban';
      loadingDetail = 'Az ingyenes hosting miatt akár 3–4 perc is lehet. Ne zárd be az oldalt — automatikusan be fog töltődni.';
    } else if (loadingDuration >= 10) {
      loadingTitle = 'Betöltés — a szerver felébresztése folyamatban';
      loadingDetail = 'Ez akár 1–2 percet is igénybe vehet az ingyenes hosting miatt.';
    }
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#00BCD4] mx-auto mb-6"></div>
          <p className="text-gray-900 text-lg font-semibold mb-2">{loadingTitle}</p>
          {loadingDetail && (
            <p className="text-gray-600 text-sm mb-4">{loadingDetail}</p>
          )}
          {/* Indeterminate progress bar — pure visual feedback that the UI is alive */}
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-4">
            <div className="h-full bg-[#00BCD4] animate-pulse" style={{ width: '40%' }}></div>
          </div>
          <p className="text-gray-400 text-xs mt-3">{loadingDuration}s</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black text-gray-900 mb-2">
                Admin Panel
              </h1>
              <p className="text-gray-600">Rendelések kezelése - Boldog Büfé</p>
            </div>
            {/* Live Clock */}
            <div className="flex items-center space-x-2 bg-white px-6 py-3 rounded-xl shadow-md border-2 border-gray-200">
              <Clock className="w-6 h-6 text-[#00BCD4]" />
              <span className="text-2xl font-bold text-gray-900">
                {currentTime.toLocaleTimeString('hu-HU')}
              </span>
            </div>
          </div>
        </div>

        {/* Restaurant Control Panel */}
        <div className="bg-white rounded-2xl p-6 shadow-md mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Étterem vezérlőpult</h2>
          <p className="text-gray-500 text-sm mb-4">Minden beállítás kizárólag manuálisan vezérelt — nincs automatikus nyitvatartás.</p>

          {/* At-a-glance summary bar — owner can read the full state in one line */}
          <div className="flex flex-wrap items-center gap-2 mb-6 p-3 bg-gray-50 border border-gray-200 rounded-xl">
            <span className="text-sm font-semibold text-gray-700 mr-2">Jelenlegi állapot:</span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold text-white ${restaurantOpen ? 'bg-green-600' : 'bg-red-600'}`}>
              {restaurantOpen ? 'NYITVA' : 'ZÁRVA'}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-sm text-gray-700">Elvitel:</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${takeawayEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
              {takeawayEnabled ? 'ENGEDÉLYEZVE' : 'LETILTVA'}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-sm text-gray-700">Helyben:</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${dineinEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
              {dineinEnabled ? 'ENGEDÉLYEZVE' : 'LETILTVA'}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-sm text-gray-700">Házhozszállítás:</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${deliveryEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
              {deliveryEnabled ? 'ENGEDÉLYEZVE' : 'LETILTVA'}
            </span>
          </div>

          {/* Master open/close */}
          <div className={`flex items-center justify-between p-4 rounded-xl border-4 mb-4 shadow-sm ${restaurantOpen ? 'border-green-500 bg-green-100' : 'border-red-500 bg-red-100'}`}>
            <div>
              <p className="font-bold text-gray-900 text-lg">Online rendelés</p>
              <p className="text-sm text-gray-700">Ha zárva, az összes rendelési lehetőség le van tiltva</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-base font-bold text-white shadow ${restaurantOpen ? 'bg-green-600' : 'bg-red-600'}`}>
                {restaurantOpen ? 'NYITVA' : 'ZÁRVA'}
              </span>
              <button
                onClick={() => updateStatus(
                  { isOpen: !restaurantOpen },
                  !restaurantOpen ? 'Online rendelés engedélyezve' : 'Online rendelés letiltva'
                )}
                className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  restaurantOpen ? 'bg-green-500 focus:ring-green-500' : 'bg-red-400 focus:ring-red-400'
                }`}
              >
                <span className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${restaurantOpen ? 'translate-x-12' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          {/* Order type toggles (always visible under master toggle) */}
          <div className="space-y-3">
            {/* Takeaway */}
            <div className={`flex items-center justify-between p-4 rounded-xl border-4 ${takeawayEnabled ? 'border-green-500 bg-green-100' : 'border-red-500 bg-red-100'}`}>
              <div>
                <p className="font-bold text-gray-900 text-lg">Elvitel</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold text-white shadow ${takeawayEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
                  {takeawayEnabled ? 'ENGEDÉLYEZVE' : 'LETILTVA'}
                </span>
                <button
                  onClick={() => updateStatus(
                    { takeawayEnabled: !takeawayEnabled },
                    !takeawayEnabled ? 'Elvitel engedélyezve' : 'Elvitel letiltva'
                  )}
                  className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    takeawayEnabled ? 'bg-green-500 focus:ring-green-500' : 'bg-red-400 focus:ring-red-400'
                  }`}
                >
                  <span className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${takeawayEnabled ? 'translate-x-12' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {/* Dine-in */}
            <div className={`flex items-center justify-between p-4 rounded-xl border-4 ${dineinEnabled ? 'border-green-500 bg-green-100' : 'border-red-500 bg-red-100'}`}>
              <div>
                <p className="font-bold text-gray-900 text-lg">Helyben fogyasztás</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold text-white shadow ${dineinEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
                  {dineinEnabled ? 'ENGEDÉLYEZVE' : 'LETILTVA'}
                </span>
                <button
                  onClick={() => updateStatus(
                    { dineinEnabled: !dineinEnabled },
                    !dineinEnabled ? 'Helyben fogyasztás engedélyezve' : 'Helyben fogyasztás letiltva'
                  )}
                  className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    dineinEnabled ? 'bg-green-500 focus:ring-green-500' : 'bg-red-400 focus:ring-red-400'
                  }`}
                >
                  <span className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${dineinEnabled ? 'translate-x-12' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {/* Delivery */}
            <div className={`flex items-center justify-between p-4 rounded-xl border-4 ${deliveryEnabled ? 'border-green-500 bg-green-100' : 'border-red-500 bg-red-100'}`}>
              <div>
                <p className="font-bold text-gray-900 text-lg">Házhozszállítás</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold text-white shadow ${deliveryEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
                  {deliveryEnabled ? 'ENGEDÉLYEZVE' : 'LETILTVA'}
                </span>
                <button
                  onClick={() => updateStatus(
                    { deliveryEnabled: !deliveryEnabled },
                    !deliveryEnabled ? 'Házhozszállítás engedélyezve' : 'Házhozszállítás letiltva'
                  )}
                  className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    deliveryEnabled ? 'bg-green-500 focus:ring-green-500' : 'bg-red-400 focus:ring-red-400'
                  }`}
                >
                  <span className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${deliveryEnabled ? 'translate-x-12' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>

          {!restaurantOpen && (
            <div className="mt-4 bg-red-50 border border-red-300 rounded-lg p-3">
              <p className="text-sm text-red-800 font-semibold">
                🔴 Az étterem zárva — az ügyfelek nem tudnak rendelni, a módtoggle-ök nem aktívak.
              </p>
            </div>
          )}
        </div>

        {/* Vacation Management ("Szabadságkezelés") */}
        <div className="bg-white rounded-2xl p-6 shadow-md mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Palmtree className="w-6 h-6 text-[#00BCD4]" />
            <h2 className="text-2xl font-bold text-gray-900">Szabadságkezelés</h2>
          </div>
          <p className="text-gray-500 text-sm mb-4">
            Add meg a szabadság kezdő és záró dátumát, majd kapcsold be. Bekapcsolt állapotban
            minden látogató egy pop-up értesítést lát a weboldalon.
          </p>

          <div className={`flex flex-wrap items-end gap-4 p-4 rounded-xl border-4 ${vacationEnabled ? 'border-[#00BCD4] bg-cyan-50' : 'border-gray-200 bg-gray-50'}`}>
            {/* Start date */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-1">Szabadság kezdete</label>
              <input
                type="date"
                value={vacationStart}
                onChange={(e) => handleVacationDate('vacationStart', e.target.value)}
                className="px-3 py-2 border-2 border-gray-300 rounded-lg outline-none focus:border-[#00BCD4]"
              />
            </div>

            {/* End date */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-1">Szabadság vége</label>
              <input
                type="date"
                value={vacationEnd}
                min={vacationStart || undefined}
                onChange={(e) => handleVacationDate('vacationEnd', e.target.value)}
                className="px-3 py-2 border-2 border-gray-300 rounded-lg outline-none focus:border-[#00BCD4]"
              />
            </div>

            {/* On/off toggle */}
            <div className="flex items-center space-x-3 ml-auto">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold text-white shadow ${vacationEnabled ? 'bg-[#00BCD4]' : 'bg-gray-400'}`}>
                {vacationEnabled ? 'BEKAPCSOLVA' : 'KIKAPCSOLVA'}
              </span>
              <button
                onClick={toggleVacation}
                className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${vacationEnabled ? 'bg-[#00BCD4] focus:ring-[#00BCD4]' : 'bg-gray-300 focus:ring-gray-400'}`}
                aria-label="Szabadság be/ki kapcsolása"
              >
                <span className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${vacationEnabled ? 'translate-x-12' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          {vacationEnabled && (
            <div className="mt-4 bg-cyan-50 border border-[#00BCD4] rounded-lg p-3">
              <p className="text-sm text-[#00838F] font-semibold">
                🌴 A szabadság aktív — a vásárlók pop-up értesítést látnak
                {vacationStart && vacationEnd ? ` (${vacationStart} – ${vacationEnd})` : ''}.
              </p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Mai rendelések</p>
                <p className="text-3xl font-black text-gray-900">{stats.totalOrders}</p>
              </div>
              <Package className="w-10 h-10 text-[#00BCD4]" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Mai bevétel</p>
                <p className="text-3xl font-black text-green-600">{stats.totalRevenue.toLocaleString()} Ft</p>
              </div>
              <CreditCard className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Függőben</p>
                <p className="text-3xl font-black text-yellow-600">{stats.pendingOrders}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Teljesítve</p>
                <p className="text-3xl font-black text-[#00BCD4]">{stats.completedOrders}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-[#00BCD4]" />
            </div>
          </div>
        </div>

        {/* Product Management Toggle */}
        <div className="mb-8">
          <button
            onClick={() => {
              setShowProductManagement(!showProductManagement);
              if (!showProductManagement && products.length === 0) {
                fetchProducts();
              }
            }}
            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-3 rounded-lg font-bold shadow-md hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Package className="w-5 h-5" />
            <span>{showProductManagement ? 'Termékkezelés Bezárása' : 'Termékkezelés Megnyitása'}</span>
          </button>
        </div>

        {/* Product Management Section */}
        {showProductManagement && (
          <div className="bg-white rounded-2xl p-6 shadow-md mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Termékkezelés
            </h2>
            <p className="text-gray-600 mb-6">
              Kattints egy termék <strong>nevére, árára vagy leírására</strong> a szerkesztéshez.
              A kikapcsolt termékek "Jelenleg nem elérhető" jelzéssel jelennek meg az étlapon.
              Új termék hozzáadásához használd a kategória alján lévő gombot.
            </p>

            {loadingProducts ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
                <p className="text-gray-600 mt-4">Termékek betöltése...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {products.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-xl p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{category.name}</h3>
                    <div className="space-y-2">
                      {category.items.map((item) => {
                        const available = item.isAvailable !== false;
                        return (
                          <div
                            key={item.id}
                            className={`p-3 rounded-lg border-2 ${
                              available ? 'bg-gray-50 border-transparent' : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-[200px]">
                                <div className="font-semibold text-gray-900">
                                  <InlineEditableField
                                    value={item.name}
                                    onSave={(v) => updateProductField(item.id, 'name', v)}
                                    placeholder="Termék neve"
                                    ariaLabel="Termék neve"
                                  />
                                </div>
                                <div className="text-sm text-[#FFA726] font-bold mt-1">
                                  <InlineEditableField
                                    value={item.price}
                                    onSave={(v) => updateProductField(item.id, 'price', v)}
                                    type="number"
                                    suffix=" Ft"
                                    ariaLabel="Termék ára"
                                  />
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  <InlineEditableField
                                    value={item.description}
                                    onSave={(v) => updateProductField(item.id, 'description', v)}
                                    placeholder="(nincs leírás)"
                                    multiline
                                    className="w-full"
                                    ariaLabel="Termék leírása"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => toggleProductAvailability(item.id, available)}
                                  className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                                    available ? 'bg-green-500' : 'bg-gray-400'
                                  }`}
                                  title={available ? 'Kapcsold ki' : 'Kapcsold be'}
                                >
                                  <span
                                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                                      available ? 'translate-x-9' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                                <span className={`text-sm font-semibold min-w-[90px] ${
                                  available ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {available ? 'Elérhető' : 'Nem elérhető'}
                                </span>
                                <button
                                  onClick={() => deleteProduct(item.id, item.name)}
                                  className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                  title="Termék törlése"
                                  aria-label="Termék törlése"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <NewProductForm categoryId={category.id} onCreate={createProduct} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'accepted', 'confirmed', 'preparing', 'ready', 'completed', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  selectedStatus === status
                    ? 'bg-[#00BCD4] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {status === 'all' ? 'Összes' : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-md">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Nincsenek rendelések ebben a kategóriában</p>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.orderNumber} className="bg-white rounded-2xl shadow-md overflow-hidden">
                <div className="p-6">
                  {/* Order Header */}
                  <div className="flex flex-wrap items-start justify-between mb-4">
                    <div className="mb-4 sm:mb-0">
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">
                        #{order.orderNumber}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleString('hu-HU', { timeZone: ORDER_TIMEZONE })}
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold">
                        {getDeliveryTypeLabel(order.deliveryType)}
                      </span>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-start space-x-3">
                      <Phone className="w-5 h-5 text-[#00BCD4] mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-gray-900">{order.customerName}</p>
                        <p className="text-gray-600">{order.customerPhone}</p>
                        {order.customerEmail && (
                          <p className="text-sm text-gray-500">{order.customerEmail}</p>
                        )}
                      </div>
                    </div>

                    {order.deliveryType === 'delivery' && (order.deliveryAddress || order.deliveryZone) && (
                      <div className="flex items-start space-x-3 bg-amber-50 p-4 rounded-xl border-2 border-amber-300">
                        <MapPin className="w-6 h-6 text-[#FFA726] mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 mb-1">📍 Szállítási cím:</p>
                          {order.deliveryZone && (
                            <p className="text-sm font-semibold text-amber-800 mb-1">
                              Zóna: {order.deliveryZone}
                            </p>
                          )}
                          {order.deliveryAddress && (
                            <p className="text-gray-900 font-medium">{order.deliveryAddress}</p>
                          )}
                          {order.deliveryFee > 0 && (
                            <p className="text-sm text-gray-600 mt-2">
                              Szállítási díj: <span className="font-bold text-[#FFA726]">{order.deliveryFee} Ft</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div className="flex items-center space-x-2 mb-4">
                    {order.paymentMethod === 'cash' ? (
                      <Wallet className="w-5 h-5 text-green-600" />
                    ) : (
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    )}
                    <span className="text-sm font-semibold text-gray-700">
                      {order.paymentMethod === 'cash' && 'Készpénz átvételkor'}
                      {order.paymentMethod === 'card' && 'Bankkártya átvételkor'}
                      {order.paymentMethod === 'szep_otp' && 'OTP SZÉP-kártya átvételkor'}
                    </span>
                  </div>

                  {/* Order Items */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <h4 className="font-bold text-gray-900 mb-3">Rendelt tételek:</h4>
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {item.quantity}x {item.name}
                            </p>
                            {item.configuration && item.configuration.options && item.configuration.options.length > 0 && (
                              <p className="text-sm text-gray-600">
                                {item.configuration.options.join(', ')}
                              </p>
                            )}
                            {item.selectedOptions && item.selectedOptions.length > 0 && (
                              <p className="text-sm text-gray-600">
                                + {item.selectedOptions.join(', ')}
                              </p>
                            )}
                            {item.selectedExtras && item.selectedExtras.length > 0 && (
                              <p className="text-sm text-blue-600">
                                Extrák: {item.selectedExtras.join(', ')}
                              </p>
                            )}
                            {item.selectedSpice && item.selectedSpice !== 'Normál' && (
                              <p className="text-sm text-red-600">
                                🌶️ {item.selectedSpice}
                              </p>
                            )}
                            {item.customerNote && (
                              <p className="text-sm text-orange-600 font-bold mt-1 bg-yellow-50 p-2 rounded border border-yellow-300">
                                ⚠️ MEGJEGYZÉS: {item.customerNote}
                              </p>
                            )}
                          </div>
                          <p className="font-bold text-gray-900">
                            {(item.price * item.quantity).toLocaleString()} Ft
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Végösszeg:</span>
                      <span className="text-2xl font-black text-[#FFA726]">
                        {order.total.toLocaleString()} Ft
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  {order.notes && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                      <p className="font-semibold text-gray-900 mb-1">Megjegyzés:</p>
                      <p className="text-gray-700">{order.notes}</p>
                    </div>
                  )}

                  {/* Status Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {/* Print Button - Always Available */}
                    <button
                      onClick={() => handlePrintOrder(order)}
                      className="px-4 py-3 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-lg font-bold hover:shadow-lg transition-all flex items-center justify-center space-x-2"
                    >
                      <Printer className="w-5 h-5" />
                      <span>Nyomtatás</span>
                    </button>

                    {order.status !== 'completed' && order.status !== 'cancelled' && order.status !== 'rejected' && (
                    <>
                      {order.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateOrderStatus(order.orderNumber, 'accepted')}
                            className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                            <span>Elfogadás</span>
                          </button>
                          <button
                            onClick={() => updateOrderStatus(order.orderNumber, 'rejected')}
                            className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
                          >
                            <XCircle className="w-5 h-5" />
                            <span>Visszautasítás</span>
                          </button>
                        </>
                      )}
                      {order.status === 'accepted' && (
                        <button
                          onClick={() => updateOrderStatus(order.orderNumber, 'preparing')}
                          className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors"
                        >
                          Készítés megkezdése
                        </button>
                      )}
                      {order.status === 'confirmed' && (
                        <button
                          onClick={() => updateOrderStatus(order.orderNumber, 'preparing')}
                          className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors"
                        >
                          Készítés megkezdése
                        </button>
                      )}
                      {order.status === 'preparing' && (
                        <button
                          onClick={() => updateOrderStatus(order.orderNumber, 'ready')}
                          className="w-full px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors"
                        >
                          Kész - Átvehető
                        </button>
                      )}
                      {order.status === 'ready' && (
                        <button
                          onClick={() => updateOrderStatus(order.orderNumber, 'completed')}
                          className="w-full px-4 py-2 bg-[#00BCD4] text-white rounded-lg font-semibold hover:bg-[#00ACC1] transition-colors"
                        >
                          Átadva / Kiszállítva
                        </button>
                      )}
                    </>
                  )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
};

export default Admin;
